import {
  WCAG_AA_TEXT_CONTRAST,
  contrastRatio,
  relativeLuminance,
} from './contrastAwareThemeEngine';
import { visualAuthority } from './visualAuthority';

const HERO_SELECTOR = [
  '[data-og-hero-adaptive="true"]',
  '.og-hero',
  '.og-runtime-hero',
  '.og-template-hero',
  '.og-template-catalog-hero',
  '.og-saas-replica-hero',
  'section[class*="hero"]',
  'section[class*="Hero"]',
].join(',');

const TEXT_SELECTOR = [
  '[data-og-hero-role="heroPrimaryText"]',
  '[data-og-hero-role="heroSecondaryText"]',
  'h1',
  'h2',
  '.headline',
  '[class*="headline"]',
  '[class*="title"]',
  '[class*="Title"]',
].join(',');

const CTA_SELECTOR = [
  '[data-og-hero-role="heroCTA"]',
  '.og-actions',
  '[class*="actions"]',
  '[class*="Actions"]',
  'a[href]',
  'button',
].join(',');

const VISUAL_SELECTOR = [
  '[data-og-hero-role="heroVisual"]',
  '[data-og-hero-role="heroDashboardPreview"]',
  '.og-slide-panel',
  '.og-runtime-supporting-visual',
  '[class*="dashboard"]',
  '[class*="Dashboard"]',
  '[class*="media"]',
  '[class*="Media"]',
  'img',
  'video',
  'canvas',
].join(',');

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const asArray = (value) => Array.from(value || []);

const textLength = (root) => String(root?.innerText || root?.textContent || '').replace(/\s+/g, ' ').trim().length;

const setRoleIfMissing = (element, role) => {
  if (element && !element.dataset.ogHeroRole) {
    element.dataset.ogHeroRole = role;
  }
};

const firstDatasetValue = (element, keys = []) => {
  if (!element?.dataset) {
    return '';
  }
  return keys.map((key) => element.dataset[key]).find((value) => String(value || '').trim()) || '';
};

const heroConfig = (hero) => ({
  layoutMode: firstDatasetValue(hero, ['ogHeroPreferredLayoutMode', 'ogHeroLayoutModePreference']),
  safeZone: firstDatasetValue(hero, ['ogHeroPreferredSafeZone', 'ogHeroSafeZonePreference']),
  overlay: firstDatasetValue(hero, ['ogHeroOverlayOverride']),
  foreground: firstDatasetValue(hero, ['ogHeroForegroundOverride']),
  textScale: firstDatasetValue(hero, ['ogHeroTextScaleOverride']),
  copyMax: firstDatasetValue(hero, ['ogHeroCopyMaxOverride']),
});

const findHeroImage = (hero) => (
  asArray(hero.querySelectorAll('img')).find((image) => (
    image instanceof HTMLImageElement && image.complete && image.naturalWidth > 12 && image.naturalHeight > 12
  )) || null
);

const regionDefinitions = Object.freeze([
  { key: 'left', x: 0, y: 0.08, width: 0.42, height: 0.84 },
  { key: 'center', x: 0.22, y: 0.12, width: 0.56, height: 0.76 },
  { key: 'right', x: 0.58, y: 0.08, width: 0.42, height: 0.84 },
  { key: 'top', x: 0.1, y: 0, width: 0.8, height: 0.38 },
  { key: 'bottom', x: 0.1, y: 0.62, width: 0.8, height: 0.38 },
]);

const fallbackAnalysis = (hero) => {
  const hasImage = Boolean(hero?.querySelector?.('img, video, canvas, .og-slide-panel, .og-runtime-supporting-visual'));
  const length = textLength(hero);
  return {
    safeZone: hasImage ? 'left' : 'center',
    tone: 'mixed',
    foreground: 'var(--og-text-on-image, var(--opengrit-text-color, #0f172a))',
    overlay: hasImage ? 'linear-gradient(90deg, rgba(15,23,42,0.72) 0%, rgba(15,23,42,0.46) 38%, rgba(15,23,42,0.08) 72%)' : 'none',
    busy: hasImage ? 0.48 : 0,
    confidence: hasImage ? 0.35 : 0.75,
    layoutMode: hasImage ? 'split-screen' : 'text-center',
    textScale: length > 220 ? 0.82 : length > 140 ? 0.9 : 1,
  };
};

export const analyzeHeroImageComposition = (image) => {
  if (!image || typeof document === 'undefined') {
    return null;
  }
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return null;
  }

  const sampleWidth = 90;
  const sampleHeight = 60;
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;

  try {
    context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    const pixelAt = (x, y) => {
      const index = ((y * sampleWidth) + x) * 4;
      return [data[index], data[index + 1], data[index + 2]];
    };

    const regions = regionDefinitions.map((region) => {
      const startX = Math.floor(region.x * sampleWidth);
      const startY = Math.floor(region.y * sampleHeight);
      const endX = Math.ceil((region.x + region.width) * sampleWidth);
      const endY = Math.ceil((region.y + region.height) * sampleHeight);
      const luminances = [];
      let edgeEnergy = 0;
      let red = 0;
      let green = 0;
      let blue = 0;
      let count = 0;

      for (let y = startY; y < endY; y += 2) {
        for (let x = startX; x < endX; x += 2) {
          const rgb = pixelAt(clamp(x, 0, sampleWidth - 1), clamp(y, 0, sampleHeight - 1));
          const luminance = relativeLuminance(rgb);
          const next = pixelAt(clamp(x + 2, 0, sampleWidth - 1), clamp(y + 2, 0, sampleHeight - 1));
          edgeEnergy += Math.abs(luminance - relativeLuminance(next));
          luminances.push(luminance);
          red += rgb[0];
          green += rgb[1];
          blue += rgb[2];
          count += 1;
        }
      }

      const average = luminances.reduce((sum, value) => sum + value, 0) / Math.max(1, luminances.length);
      const variance = luminances.reduce((sum, value) => sum + ((value - average) ** 2), 0) / Math.max(1, luminances.length);
      const busy = clamp((variance * 5) + (edgeEnergy / Math.max(1, luminances.length)) * 2.4, 0, 1);
      const background = `rgb(${Math.round(red / count)}, ${Math.round(green / count)}, ${Math.round(blue / count)})`;
      const whiteContrast = contrastRatio('#ffffff', background);
      const darkContrast = contrastRatio('#0f172a', background);
      const foreground = whiteContrast >= darkContrast ? '#ffffff' : '#0f172a';
      const readability = Math.max(whiteContrast, darkContrast) / WCAG_AA_TEXT_CONTRAST;
      const whitespace = 1 - busy;
      const score = (whitespace * 0.58) + (Math.min(readability, 2.2) / 2.2) * 0.42;

      return {
        key: region.key,
        luminance: average,
        busy,
        background,
        foreground,
        score,
      };
    });

    const best = [...regions].sort((left, right) => right.score - left.score)[0];
    const tone = best.luminance < 0.38 ? 'dark' : best.luminance > 0.72 ? 'light' : 'mixed';
    const overlayDirection = best.key === 'right' ? '270deg' : best.key === 'center' ? '180deg' : '90deg';
    return {
      safeZone: best.key,
      tone,
      foreground: best.foreground,
      overlay: best.foreground === '#ffffff'
        ? `linear-gradient(${overlayDirection}, rgba(15,23,42,0.74), rgba(15,23,42,0.34) 48%, rgba(15,23,42,0.04))`
        : `linear-gradient(${overlayDirection}, rgba(255,255,255,0.84), rgba(255,255,255,0.42) 48%, rgba(255,255,255,0.04))`,
      busy: best.busy,
      confidence: clamp(best.score, 0, 1),
      regions,
    };
  } catch (error) {
    return null;
  }
};

export const resolveHeroComposition = (hero, analysis = fallbackAnalysis(hero)) => {
  const rect = hero?.getBoundingClientRect?.() || { width: 0 };
  const width = rect.width || window.innerWidth || 0;
  const length = textLength(hero);
  const hasVisual = Boolean(hero?.querySelector?.(VISUAL_SELECTOR));
  const hasDashboard = Boolean(hero?.querySelector?.('[data-og-hero-role="heroDashboardPreview"], .og-slide-panel, [class*="dashboard"], [class*="Dashboard"]'));
  const safeZone = analysis.safeZone || 'left';
  const textScale = clamp(
    (length > 260 ? 0.74 : length > 190 ? 0.82 : length > 130 ? 0.9 : 1)
    - (analysis.busy > 0.62 ? 0.08 : 0),
    0.72,
    1,
  );

  if (width < 720) {
    return { layoutMode: 'stacked-readable', safeZone: 'center', textScale };
  }
  if (!hasVisual) {
    return { layoutMode: 'text-center', safeZone: 'center', textScale };
  }
  if (hasDashboard) {
    return { layoutMode: 'dashboard-preview', safeZone: safeZone === 'right' ? 'left' : safeZone, textScale };
  }
  if (safeZone === 'center' && analysis.confidence > 0.62) {
    return { layoutMode: 'cinematic', safeZone, textScale };
  }
  if (safeZone === 'right') {
    return { layoutMode: 'text-right', safeZone, textScale };
  }
  return { layoutMode: 'text-left', safeZone: safeZone === 'bottom' || safeZone === 'top' ? 'center' : safeZone, textScale };
};

export const applyHeroSafeZone = (hero) => {
  if (!hero || hero.dataset.ogHeroEngineLocked === 'true') {
    return null;
  }
  const image = findHeroImage(hero);
  const config = heroConfig(hero);
  const imageAnalysis = analyzeHeroImageComposition(image);
  const analysis = {
    ...(imageAnalysis || fallbackAnalysis(hero)),
    ...(config.safeZone ? { safeZone: config.safeZone } : {}),
    ...(config.overlay ? { overlay: config.overlay } : {}),
    ...(config.foreground ? { foreground: config.foreground } : {}),
  };
  const composition = {
    ...resolveHeroComposition(hero, analysis),
    ...(config.layoutMode ? { layoutMode: config.layoutMode } : {}),
    ...(config.safeZone ? { safeZone: config.safeZone } : {}),
    ...(config.textScale ? { textScale: Number(config.textScale) || 1 } : {}),
  };

  const primaryText = hero.querySelector('h1, h2, [class*="headline"], [class*="Headline"]');
  const secondaryText = hero.querySelector('p, [class*="body"], [class*="Body"], [class*="copy"], [class*="Copy"]');
  const cta = hero.querySelector('.og-actions, [class*="actions"], [class*="Actions"]');
  const visual = hero.querySelector('.og-slide-panel, .og-runtime-supporting-visual, img, video, canvas, [class*="media"], [class*="Media"]');

  setRoleIfMissing(primaryText, 'heroPrimaryText');
  setRoleIfMissing(secondaryText, 'heroSecondaryText');
  setRoleIfMissing(cta, 'heroCTA');
  setRoleIfMissing(visual, visual?.className && String(visual.className).includes('dashboard') ? 'heroDashboardPreview' : 'heroVisual');

  hero.dataset.ogHeroAdaptive = 'true';
  hero.dataset.ogHeroSafeZone = composition.safeZone;
  hero.dataset.ogHeroLayoutMode = composition.layoutMode;
  hero.dataset.ogHeroTone = analysis.tone;
  hero.dataset.ogHeroBusy = analysis.busy > 0.6 ? 'busy' : 'calm';
  visualAuthority.requestStyles('HeroSafeZoneEngine', hero, {
    '--og-hero-safe-foreground': analysis.foreground,
    '--og-hero-overlay': analysis.overlay,
    '--og-hero-text-scale': String(composition.textScale),
    '--og-hero-copy-max': config.copyMax || `${clamp(760 - textLength(hero) * 0.8, 520, 760)}px`,
  });

  validateHeroComposition(hero);
  return { ...analysis, ...composition };
};

export const validateHeroComposition = (hero) => {
  if (!hero || typeof window === 'undefined') {
    return { valid: true, violations: [] };
  }
  const heroRect = hero.getBoundingClientRect();
  const elements = asArray(hero.querySelectorAll(TEXT_SELECTOR))
    .concat(asArray(hero.querySelectorAll(CTA_SELECTOR)))
    .filter((element, index, list) => element && list.indexOf(element) === index);
  const visual = hero.querySelector(VISUAL_SELECTOR);
  const visualRect = visual?.getBoundingClientRect?.();
  const violations = [];

  elements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.width > heroRect.width + 1 || rect.right > heroRect.right + 2 || rect.left < heroRect.left - 2) {
      violations.push('typography-overflow');
    }
    if (visualRect && rect.width && rect.height) {
      const overlapX = Math.max(0, Math.min(rect.right, visualRect.right) - Math.max(rect.left, visualRect.left));
      const overlapY = Math.max(0, Math.min(rect.bottom, visualRect.bottom) - Math.max(rect.top, visualRect.top));
      const overlapArea = overlapX * overlapY;
      if (overlapArea > rect.width * rect.height * 0.18 && hero.dataset.ogHeroLayoutMode !== 'cinematic') {
        violations.push('image-safe-zone-violated');
      }
    }
  });

  hero.dataset.ogHeroGovernance = violations.length ? 'fail' : 'pass';
  hero.dataset.ogHeroViolations = violations.join(',');
  return { valid: violations.length === 0, violations };
};

export const scanAdaptiveHeroes = (root = document) => (
  asArray(root.querySelectorAll?.(HERO_SELECTOR)).map(applyHeroSafeZone).filter(Boolean)
);

export const installHeroSafeZoneEngine = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.__opengritHeroSafeZoneEngineInstalled) {
    return () => {};
  }
  window.__opengritHeroSafeZoneEngineInstalled = true;

  let frame = 0;
  const schedule = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => scanAdaptiveHeroes(document));
  };

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { passive: true });
  document.addEventListener('input', schedule, true);
  document.addEventListener('change', schedule, true);
  asArray(document.images).forEach((image) => {
    if (!image.complete) {
      image.addEventListener('load', schedule, { once: true });
      image.addEventListener('error', schedule, { once: true });
    }
  });

  const observer = new MutationObserver(schedule);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'class', 'data-og-hero-adaptive'],
    characterData: true,
  });
  schedule();

  return () => {
    window.cancelAnimationFrame(frame);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('load', schedule);
    document.removeEventListener('input', schedule, true);
    document.removeEventListener('change', schedule, true);
    observer.disconnect();
    window.__opengritHeroSafeZoneEngineInstalled = false;
  };
};

export const HeroSafeZoneEngine = {
  analyzeHeroImageComposition,
  resolveHeroComposition,
  applyHeroSafeZone,
  validateHeroComposition,
  scanAdaptiveHeroes,
  installHeroSafeZoneEngine,
};
